import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import Modal from '../components/ui/Modal';
import Select from '../components/ui/Select';
import { fetchTransactionStats, fetchTransactions, fetchMerchantProducts } from '../services/api';
import { B2B_PRODUCTS } from '../utils/products';
import {
  Search,
  Download,
  Filter,
  Printer,
  CreditCard,
  Smartphone,
  ArrowUpRight,
  Receipt,
  Users,
  Building2,
  LayoutGrid,
  ChevronRight,
  TrendingUp,
  Clock,
  CheckCircle2,
  XCircle,
  FileText
} from 'lucide-react';
import { useAuthStore } from '../store/useAuthStore';
import { PERMISSIONS } from '../utils/permissions';
import HasPermission from '../components/HasPermission';

const TransactionsPage = () => {
  const [activeTab, setActiveTab] = useState(null);
  const [subscribedTabs, setSubscribedTabs] = useState([]);
  const [loadingSubs, setLoadingSubs] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTx, setSelectedTx] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const { user } = useAuthStore();

  useEffect(() => {
    const loadSubscriptions = async () => {
      if (!user?.merchantId) return;
      setLoadingSubs(true);
      try {
        const response = await fetchMerchantProducts();
        const myRequests = response.data?.data || response.data || [];

        const activeSubCodes = myRequests
          .filter(req => req.status === 'APPROVED' || req.subscriptionStatus === 'ACTIVE')
          .map(req => req.productCode);

        const iconMap = {
          'dynamic-va': Smartphone,
          'static-va': Smartphone,
          'payout': ArrowUpRight,
          'pay-with-waya': Building2,
          'bills-payment': Receipt,
          'bulk-transfer': Users,
          'pos': CreditCard
        };

        const filteredTabs = B2B_PRODUCTS
          .filter(p => activeSubCodes.includes(p.productCode))
          .map(p => ({
            id: p.id,
            label: p.title,
            icon: iconMap[p.id] || LayoutGrid,
            productCode: p.productCode
          }));

        setSubscribedTabs(filteredTabs);
        if (filteredTabs.length > 0) {
          setActiveTab(filteredTabs[0].id);
        }
      } catch (err) {
        console.error('Error fetching subscriptions:', err);
      } finally {
        setLoadingSubs(false);
      }
    };
    loadSubscriptions();
  }, [user?.merchantId]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.merchantId || !activeTab) return;

      try {
        const productCode = subscribedTabs.find(t => t.id === activeTab)?.productCode;
        if (!productCode) return;

        const [statsRes, transRes] = await Promise.all([
          fetchTransactionStats({ merchantId: user.merchantId, productCode }),
          fetchTransactions({
            merchantId: user.merchantId,
            productCode,
            page: 0,
            size: 50,
            sortBy: 'transactionDate',
            sortDir: 'DESC'
          })
        ]);

        if (statsRes.data.status) {
          setStats(statsRes.data.data);
        }

        if (transRes.data.status) {
          setTransactions(transRes.data.data.content || transRes.data.data || []);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    fetchData();
  }, [user?.merchantId, activeTab, subscribedTabs]);

  const statistics = useMemo(() => {
    if (!stats) return [
      { label: 'Total Volume', value: '₦0', icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50' },
      { label: 'Total Transactions', value: '0', icon: LayoutGrid, color: 'text-purple-600', bg: 'bg-purple-50' },
      { label: 'Successful', value: '0', icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50' },
      { label: 'Failed', value: '0', icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
    ];

    return [
      { label: 'Total Volume', value: `₦${Number(stats.totalVolume || 0).toLocaleString()}`, icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50' },
      { label: 'Total Transactions', value: stats.totalCount || 0, icon: LayoutGrid, color: 'text-purple-600', bg: 'bg-purple-50' },
      { label: 'Successful', value: stats.successfulCount || 0, icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50' },
      { label: 'Failed', value: stats.failedCount || 0, icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
    ];
  }, [stats]);

  const getTableData = () => {
    const commonHeaders = ['Reference', 'Amount', 'Type', 'Account', 'Source/Sender', 'Date/Time', 'Status'];

    const rows = transactions.map(tx => ({
      id: tx.id || tx.reference,
      ref: tx.reference,
      amount: `₦${Number(tx.amount || 0).toLocaleString()}`,
      type: tx.type || 'N/A',
      account: tx.accountNumber || 'N/A',
      source: tx.senderName || tx.source || 'N/A',
      date: tx.transactionDate || tx.timestamp || 'N/A',
      status: tx.status
    }));

    return { headers: commonHeaders, rows };
  };

  const { headers, rows } = getTableData();

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    const csvContent = "data:text/csv;charset=utf-8,"
      + headers.join(",") + "\n"
      + rows.map(r => Object.values(r).filter((v, i) => i !== 0).join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `transactions_${activeTab}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openDetails = (tx) => {
    setSelectedTx(tx);
    setIsModalOpen(true);
  };

  if (loadingSubs) {
    return (
      <div className="h-96 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (subscribedTabs.length === 0) {
    return (
      <div className="h-96 flex flex-col items-center justify-center text-center space-y-4">
        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center text-slate-400">
          <LayoutGrid size={32} />
        </div>
        <div className="max-w-md">
          <h3 className="text-lg font-bold text-slate-900">No Subscribed Products</h3>
          <p className="text-slate-500 mt-2">
            You have not subscribed to any product. You will see transactions of products you have subscribed to once you activate them in the Products section.
          </p>
          <Link to="/products" className="mt-6 inline-block">
            <Button variant="primary">Explore Products</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12 print:p-0">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 print:hidden">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">Financial History</h2>
          <p className="text-sm text-slate-500 mt-1">Detailed overview of all your product and ledger activities.</p>
        </div>
        <div className="flex items-center gap-3">
          <HasPermission permission={PERMISSIONS.EXPORT_TRANSACTIONS}>
            <div className="flex items-center gap-3">
              <Button onClick={handlePrint} variant="secondary" className="gap-2 bg-white hover:bg-slate-50 border-slate-200">
                <Printer size={16} /> Print Report
              </Button>
              <Button onClick={handleExport} variant="primary" className="gap-2 shadow-sm">
                <Download size={16} /> Export CSV
              </Button>
            </div>
          </HasPermission>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 print:hidden">
        {statistics.map((stat, i) => (
          <Card key={i} className="border-0 shadow-sm overflow-hidden group hover:shadow-md transition-shadow">
            <CardContent className="p-5 flex items-center gap-4">
              <div className={`p-3 rounded-xl ${stat.bg} ${stat.color} transition-transform group-hover:scale-110`}>
                <stat.icon size={22} />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{stat.label}</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5">{stat.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="bg-white p-1 rounded-xl shadow-sm border border-slate-100 inline-flex flex-wrap gap-1 w-full lg:w-auto print:hidden">
        {subscribedTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${activeTab === tab.id
              ? 'bg-primary text-white shadow-md'
              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      <Card className="border-slate-100 shadow-sm">
        <div className="p-4 border-b border-slate-50 flex flex-col md:flex-row items-center gap-4 print:hidden">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by reference, amount, or recipient..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all bg-slate-50/50"
            />
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto">
            <Button variant="secondary" className="flex-1 md:flex-none gap-2 bg-white border-slate-200 text-slate-600">
              <Filter size={16} /> Filter
            </Button>
            <Select
              options={[
                { value: '30', label: 'Last 30 days' },
                { value: '7', label: 'Last 7 days' },
                { value: '365', label: 'This year' },
                { value: 'custom', label: 'Custom range' }
              ]}
              defaultValue={{ value: '30', label: 'Last 30 days' }}
              className="flex-1 md:flex-none w-full md:w-44"
            />
          </div>
        </div>

        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100">
                  {headers.map((header) => (
                    <th key={header} className="px-6 py-4 font-semibold text-slate-600 uppercase tracking-wider text-[11px] first:pl-8">
                      {header}
                    </th>
                  ))}
                  <th className="px-6 py-4 print:hidden"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50/50 transition-colors group">
                    {Object.entries(row).filter(([key]) => key !== 'id').map(([key, value], idx) => (
                      <td key={idx} className="px-6 py-4 whitespace-nowrap first:pl-8">
                        {key === 'status' ? (
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide shadow-sm ${value === 'Success' ? 'bg-green-50 text-green-700 ring-1 ring-green-100' :
                            value === 'Failed' ? 'bg-red-50 text-red-700 ring-1 ring-red-100' :
                              'bg-amber-50 text-amber-700 ring-1 ring-amber-100'
                            }`}>
                            {value === 'Success' && <CheckCircle2 size={10} />}
                            {value === 'Failed' && <XCircle size={10} />}
                            {value === 'Processing' && <Clock size={10} />}
                            {value}
                          </span>
                        ) : key === 'amount' ? (
                          <span className={`font-bold ${row.type === 'Debit' ? 'text-red-500' : 'text-slate-900'}`}>
                            {value}
                          </span>
                        ) : (
                          <span className="text-slate-600 font-medium">{value}</span>
                        )}
                      </td>
                    ))}
                    <td className="px-6 py-4 text-right pr-8 print:hidden">
                      <button
                        onClick={() => openDetails(row)}
                        className="p-2 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                        title="View Details"
                      >
                        <ChevronRight size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="p-4 border-t border-slate-100 bg-slate-50/30 flex items-center justify-between print:hidden">
            <p className="text-xs text-slate-500 font-medium">Showing 1 to {rows.length} of {stats?.totalCount || rows.length} transactions</p>
            <div className="flex items-center gap-2">
              <Button disabled variant="secondary" className="px-3 py-1.5 text-xs bg-white border-slate-200">Previous</Button>
              <Button variant="secondary" className="px-3 py-1.5 text-xs bg-white border-slate-200">Next</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Transaction Details"
        size="md"
        footer={
          <div className="flex items-center justify-between w-full">
            <HasPermission permission={PERMISSIONS.EXPORT_TRANSACTIONS}>
              <Button variant="secondary" className="gap-2" onClick={handlePrint}>
                <Printer size={16} /> Print Receipt
              </Button>
            </HasPermission>
            <Button variant="primary" onClick={() => setIsModalOpen(false)}>
              Close
            </Button>
          </div>
        }
      >
        {selectedTx && (
          <div className="space-y-6">
            <div className="flex items-center justify-center flex-col p-6 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
              <div className={`p-4 rounded-full mb-3 ${selectedTx.status === 'Success' ? 'bg-green-100 text-green-600' :
                selectedTx.status === 'Failed' ? 'bg-red-100 text-red-600' :
                  'bg-amber-100 text-amber-600'
                }`}>
                {selectedTx.status === 'Success' ? <CheckCircle2 size={32} /> : <XCircle size={32} />}
              </div>
              <h4 className="text-sm font-medium text-slate-500 uppercase tracking-widest">Transaction Amount</h4>
              <p className="text-3xl font-extrabold text-slate-900 mt-1">{selectedTx.amount}</p>
              <div className="mt-4 flex gap-2">
                <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${selectedTx.status === 'Success' ? 'bg-green-500 text-white' : 'bg-slate-400 text-white'
                  }`}>
                  {selectedTx.status}
                </span>
                <span className="px-3 py-1 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold uppercase tracking-wider">
                  {selectedTx.ref}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-y-4">
              {Object.entries(selectedTx)
                .filter(([key]) => key !== 'id')
                .map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between pb-3 border-b border-slate-50 last:border-0 last:pb-0">
                    <span className="text-sm font-medium text-slate-400 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                    <span className="text-sm font-semibold text-slate-800">{value}</span>
                  </div>
                ))}
            </div>

            <div className="p-4 bg-primary/5 rounded-xl border border-primary/10 flex gap-3">
              <FileText className="text-primary shrink-0" size={20} />
              <p className="text-xs text-slate-600 leading-relaxed">
                This transaction was processed via the **{subscribedTabs.find(t => t.id === activeTab)?.label}** channel. For help, please quote the reference number when contacting support.
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TransactionsPage;

